import { matrixToPermissionStrings } from '@evi/shared';

/**
 * The Admin's private-switch editor (Warren, 2026-10-22: "Per-charger
 * private switch: same-tenant drivers must pass the pass list too; site host
 * and staff exempt"). Pure, so the rules have tests:
 *
 *   - who may edit: the permission string PUT /chargers/:id checks
 *     (Charger:update), derived from the shared resource map so it cannot
 *     drift from the server's gate, and never a Site (the route is EVI
 *     staff only, requireInternalCaller). A super_admin token passes the
 *     Api's own bypass.
 *   - the draft: the switch and the pass list as edited, against what was
 *     loaded; Save sends both plus `expected`, so the Api refuses a stale
 *     editor with 409 instead of overwriting someone else's change.
 */

/** The string Charger:update emits, what PUT and PATCH private-access check. */
export const CHARGER_WRITE_PERMISSION: string = matrixToPermissionStrings([{
  resource: 'Charger', create: false, read: false, update: true, delete: false,
}] as never)[0] ?? 'charger:write';

export interface PrivateEditor {
  role?: string | null;
  permissions?: readonly string[] | null;
}

export function canEditPrivateAccess(user: PrivateEditor | null | undefined, isSite: boolean): boolean {
  if (!user || isSite) return false;
  return user.role === 'super_admin' || (user.permissions ?? []).includes(CHARGER_WRITE_PERMISSION);
}

export interface PrivateAccessState {
  private_access: boolean;
  guest_pass_list: string[];
}

export interface PassMember {
  _id: string;
  name: string | null;
  email: string | null;
}

export const PASS_LIST_MAX = 100;
const OBJECT_ID = /^[a-f0-9]{24}$/i;

/** Add a member: a real account id, once, within the cap. */
export function addPassMember(list: readonly PassMember[], m: PassMember): { list: PassMember[]; error: string | null } {
  if (!OBJECT_ID.test(m._id)) return { list: [...list], error: 'That is not an account.' };
  if (list.some((x) => x._id.toLowerCase() === m._id.toLowerCase())) return { list: [...list], error: 'Already on the pass list.' };
  if (list.length >= PASS_LIST_MAX) return { list: [...list], error: `A pass list holds at most ${PASS_LIST_MAX} drivers.` };
  return { list: [...list, m], error: null };
}

export function removePassMember(list: readonly PassMember[], id: string): PassMember[] {
  return list.filter((x) => x._id !== id);
}

export function isPrivateDraftDirty(loaded: PrivateAccessState, draftPrivate: boolean, draftList: readonly PassMember[]): boolean {
  return draftPrivate !== loaded.private_access
    || draftList.map((m) => m._id).join(',') !== loaded.guest_pass_list.join(',');
}

/** The PATCH body: the switch and the list together (one write on the
 *  Api, so turning the switch on and adding the driver who rents the spot
 *  land at once), and what was loaded as `expected`. */
export function privateAccessPayload(loaded: PrivateAccessState, draftPrivate: boolean, draftList: readonly PassMember[]): {
  private_access: boolean;
  guest_pass_list: string[];
  expected: PrivateAccessState;
} {
  return {
    private_access: draftPrivate,
    guest_pass_list: draftList.map((m) => m._id),
    expected: { private_access: loaded.private_access, guest_pass_list: [...loaded.guest_pass_list] },
  };
}

/** A member's label: name and email, or the id when the account has neither. */
export function passMemberLabel(m: PassMember): string {
  if (m.name && m.email) return `${m.name} (${m.email})`;
  return m.name ?? m.email ?? m._id;
}

/** The warning before Save turns the switch on with an empty pass list:
 *  nobody but the host and staff could start. */
export function emptyPrivateWarning(draftPrivate: boolean, draftList: readonly PassMember[]): string | null {
  return draftPrivate && draftList.length === 0
    ? 'Nobody is on the pass list: only the site host and EVI staff will be able to start this charger.'
    : null;
}
